/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notifyInfo } from "redux/actions/Notifications";
import AppContext from "./AppContext";
import { Client } from "@twilio/conversations";
import { initializeMessaging } from "config/firebaseWithFirestore";

function MessagingInitializer() {
    const dispatch = useDispatch();
    const { loggedIn } = useSelector((state) => state.Login);
    const { messaging, setMessaging, joinConversation, leaveConversation } = useContext(AppContext);

    const setClientState = (newState) => {
        setMessaging((prev) => ({ ...prev, twilioConversations: { ...prev.twilioConversations, clientState: newState } }));
    };

    const setClientInitialized = (isInitialized) => {
        setMessaging((prev) => ({ ...prev, twilioConversations: { ...prev.twilioConversations, initialized: isInitialized } }));
    };

    const setClient = (client) => {
        setMessaging((prev) => ({ ...prev, twilioConversations: { ...prev.twilioConversations, client } }));
    };

    const setConnectionState = (newState) => {
        setMessaging((prev) => ({ ...prev, twilioConversations: { ...prev.twilioConversations, connectionState: newState } }));
    };

    const {
        api: {
            Chat: { ClientAccess },
        },
    } = useContext(AppContext);

    const initializeListeners = (client) => {
        // Connection State Listener
        // Possible values:
        //      connecting - client is offline and connection attempt is in progress
        //      connected - client is online and ready
        //      disconnecting - client is going offline as disconnection is in progress
        //      disconnected - client is offline and no connection attempt is in progress
        //      denied - client connection is denied because of invalid JWT access token. User must refresh token in order to proceed

        client.on("connectionStateChanged", (newConnectionState) => {
            setConnectionState(newConnectionState);
        });

        // Conversation Listeners
        client.on("conversationJoined", (conversation) => {
            joinConversation(conversation);
        });

        client.on("conversationLeft", (conversation) => {
            leaveConversation(conversation);
        });
    };

    const initializeClient = (access_token) => {
        // Create the client
        const client = new Client(access_token, { logLevel: "error" });

        // Client State Listener
        client.on("stateChanged", (newState) => {
            setClientState(newState);
            if (newState === "initialized") {
                setClientInitialized(true);
            }
            if (newState === "failed") {
                setClientInitialized(false);
            }
        });

        return client;
    };

    const initializeAccesstoken = async () => {
        try {
            // Request an access token
            const response = await ClientAccess.createAccessTokenAsync({ identity: messaging.profile });
            if (response && response.data && typeof response.data === "string") {
                return response.data;
            } else throw new Error("The server returned an invalid Chat Authorization Token. Please contact an administrator.");
        } catch (error) {
            console.error("Error authorizing chat.", error);
            return null;
        }
    };

    const initializePushNotifications = async (client) => {
        await initializeMessaging(client);
        client.on("pushNotification", (data) => {
            console.log("Push notification received! ", data);
            dispatch(notifyInfo(data.body));
        });
        console.log("Listening for push notifications...");
    };

    const initialize = async () => {
        setClientState("initializing");
        const token = await initializeAccesstoken();

        if (token) {
            const client = initializeClient(token);
            await initializePushNotifications(client);
            setClient(client);
            initializeListeners(client);
            console.log("Messaging initialized!");
        } else {
            setClientState("aborted");
        }
    };

    useEffect(() => {
        if (messaging.profile && loggedIn) {
            initialize();
        }
    }, [messaging.profile, loggedIn]);

    return null;
}

export default MessagingInitializer;
